import { useState, useEffect, useCallback } from 'react'
import { CategoryService } from '../services/categories'
import { AuthService } from '../services/auth'
import { initializeCategories } from '../utils/category-init'
import type { CategoryWithVideoCount, CategoryInsert } from '../services/categories'

interface CategoryFormData {
  name: string
  slug: string
  description: string
  is_active: boolean
}

const CategoryManager = () => {
  const [categories, setCategories] = useState<CategoryWithVideoCount[]>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState<CategoryWithVideoCount | null>(null)
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    slug: '',
    description: '',
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [draggedItem, setDraggedItem] = useState<string | null>(null)

  const loadCategories = useCallback(async () => {
    try {
      setIsLoading(true)
      const categoriesData = await CategoryService.getAllCategories(true)
      setCategories(categoriesData)
    } catch (error) {
      console.error('Failed to load categories:', error)
      setError('カテゴリーの読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      is_active: true
    })
    setEditingCategory(null)
    setError('')
  }

  const handleAddCategory = () => {
    resetForm()
    setShowAddForm(true)
  }

  const handleInitializeCategories = async () => {
    if (confirm('デフォルトカテゴリーを初期化しますか？既存のカテゴリーがある場合は追加されます。')) {
      try {
        setIsLoading(true)
        const result = await initializeCategories()
        if (result.success) {
          await loadCategories()
          alert(result.message)
        } else {
          setError(result.message)
        }
      } catch (error) {
        console.error('Failed to initialize categories:', error)
        setError('カテゴリーの初期化に失敗しました')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleEditCategory = (category: CategoryWithVideoCount) => {
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      is_active: category.is_active
    })
    setEditingCategory(category)
    setShowAddForm(true)
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingCategory ? prev.slug : generateSlug(name)
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      if (editingCategory) {
        // Update existing category
        await CategoryService.updateCategory(editingCategory.id, {
          name: formData.name,
          slug: formData.slug,
          description: formData.description || null,
          is_active: formData.is_active
        })
        
        await AuthService.logAdminAction('update_category', 'category', editingCategory.id, {
          name: formData.name,
          slug: formData.slug
        })
      } else {
        // Create new category
        const categoryData: CategoryInsert = {
          name: formData.name,
          slug: formData.slug,
          description: formData.description || null,
          is_active: formData.is_active
        }
        
        const newCategory = await CategoryService.createCategory(categoryData)
        
        await AuthService.logAdminAction('create_category', 'category', newCategory.id, {
          name: formData.name,
          slug: formData.slug
        })
      }

      setShowAddForm(false)
      resetForm()
      await loadCategories()
    } catch (err) {
      console.error('Failed to save category:', err)
      setError(err instanceof Error ? err.message : 'カテゴリーの保存に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteCategory = async (category: CategoryWithVideoCount) => {
    if (category.video_count > 0) {
      alert(`このカテゴリーには${category.video_count}件の動画が関連付けられています。先に動画を他のカテゴリーに移動してから削除してください。`)
      return
    }

    if (confirm(`「${category.name}」を削除してもよろしいですか？`)) {
      try {
        setIsLoading(true)
        await CategoryService.deleteCategory(category.id)
        
        await AuthService.logAdminAction('delete_category', 'category', category.id, {
          name: category.name,
          slug: category.slug
        })
        
        await loadCategories()
      } catch (error: unknown) {
        console.error('Failed to delete category:', error)
        const errorMessage = error instanceof Error ? error.message : String(error)
        setError(errorMessage || 'カテゴリーの削除に失敗しました')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleToggleActive = async (category: CategoryWithVideoCount) => {
    try {
      setIsLoading(true)
      await CategoryService.toggleActive(category.id)
      
      await AuthService.logAdminAction('toggle_category_active', 'category', category.id, {
        name: category.name,
        is_active: !category.is_active
      })
      
      await loadCategories()
    } catch (error: unknown) {
      console.error('Failed to toggle category active status:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || 'カテゴリーの状態変更に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDragStart = (e: React.DragEvent, categoryId: string) => {
    setDraggedItem(categoryId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = async (e: React.DragEvent, targetCategoryId: string) => {
    e.preventDefault()
    
    if (!draggedItem || draggedItem === targetCategoryId) {
      setDraggedItem(null)
      return
    }

    const draggedIndex = categories.findIndex(cat => cat.id === draggedItem)
    const targetIndex = categories.findIndex(cat => cat.id === targetCategoryId)

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null)
      return
    }

    // Reorder categories
    const newCategories = [...categories]
    const [draggedCategory] = newCategories.splice(draggedIndex, 1)
    newCategories.splice(targetIndex, 0, draggedCategory)

    // Update display_order
    const updateData = newCategories.map((cat, index) => ({
      id: cat.id,
      display_order: index + 1
    }))

    try {
      setIsLoading(true)
      await CategoryService.updateDisplayOrder(updateData)
      await loadCategories()
      
      await AuthService.logAdminAction('reorder_categories', 'category', draggedItem, {
        from_position: draggedIndex + 1,
        to_position: targetIndex + 1
      })
    } catch (error: unknown) {
      console.error('Failed to reorder categories:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || 'カテゴリーの並び替えに失敗しました')
    } finally {
      setIsLoading(false)
      setDraggedItem(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">カテゴリー管理</h2>
          <p className="text-sm text-gray-600">動画カテゴリーの作成、編集、並び替えができます</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleInitializeCategories}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            デフォルトカテゴリー初期化
          </button>
          <button
            onClick={handleAddCategory}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            カテゴリーを追加
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-base font-medium text-gray-900 mb-4">
            {editingCategory ? 'カテゴリーを編集' : '新しいカテゴリーを追加'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  カテゴリー名 *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例: はじめての起業"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  スラッグ *
                </label>
                <input
                  type="text"
                  required
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例: beginner"
                />
                <p className="text-xs text-gray-500 mt-1">
                  URLやデータベースで使用される識別子（英数字とハイフンのみ）
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                説明
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="カテゴリーの説明を入力してください"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                アクティブ（ユーザーに表示される）
              </label>
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                disabled={isLoading}
                className="bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                {isLoading ? '保存中...' : editingCategory ? '更新' : '作成'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-medium text-gray-900">
            カテゴリー一覧 ({categories.length}件)
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            ドラッグ&ドロップで表示順序を変更できます
          </p>
        </div>
        
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">読み込み中...</p>
          </div>
        ) : categories.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {categories.map((category, index) => (
              <div
                key={category.id}
                draggable
                onDragStart={(e) => handleDragStart(e, category.id)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, category.id)}
                className={`p-6 hover:bg-gray-50 cursor-move transition-colors ${
                  draggedItem === category.id ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                      {index + 1}
                    </div>
                    <div>
                      <div className="flex items-center space-x-3">
                        <h4 className="text-base font-medium text-gray-900">
                          {category.name}
                        </h4>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {category.slug}
                        </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          category.is_active 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {category.is_active ? 'アクティブ' : '非アクティブ'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {category.video_count}件の動画
                        </span>
                      </div>
                      {category.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {category.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleToggleActive(category)}
                      className={`px-3 py-1 rounded text-sm font-medium ${
                        category.is_active
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                    >
                      {category.is_active ? '非アクティブにする' : 'アクティブにする'}
                    </button>
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category)}
                      disabled={category.video_count > 0}
                      className={`text-sm font-medium ${
                        category.video_count > 0
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-red-600 hover:text-red-500'
                      }`}
                    >
                      削除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-500">カテゴリーがありません</p>
            <button
              onClick={handleAddCategory}
              className="mt-2 text-blue-600 hover:text-blue-500 text-sm font-medium"
            >
              最初のカテゴリーを追加
            </button>
          </div>
        )}
      </div>

      {error && !showAddForm && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
    </div>
  )
}

export default CategoryManager